import { DARK, DEFAULT_ORB_COLOR_1, DEFAULT_ORB_COLOR_2, LIGHT, paletteCSS, RADIUS } from "./tokens.js";

// Presentation constants for the <speechify-agent> element: its stylesheet,
// its icons, and the copy/colour defaults every override chain falls back to.
//
// Split out of element.ts because it is data, not behaviour. It was 560 of
// that file's lines, which meant the element's actual logic started a third
// of the way down and every stylistic tweak showed up as a diff against the
// same file as the call lifecycle.
//
// The stylesheet is a template literal rather than a CSS file because the
// bundle is a single self-contained script a customer drops on their page:
// there is no second request to make, and the styles have to land inside the
// shadow root at runtime. Watch for backticks in the CSS - one inside a
// comment terminates the literal and the failure is a confusing parse error
// hundreds of lines later.

// Inline SVG mic icon — avoids shipping an icon font or extra request.
export const MIC_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"/></svg>`;

export const MIC_MUTED_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>`;

// Default call-start / status copy. Overridden via the
// `start-call-text` / `listening-text` / `speaking-text` attributes
// so the same embed can speak any language. `end-call-text` has no
// dedicated default — it acts as a fallback for listening/speaking
// copy when the caller wants a single "End call" label instead of
// per-state text (see #render).
export const DEFAULT_START_CALL_TEXT = "Talk to agent";
export const DEFAULT_LISTENING_TEXT = "Listening — tap to end";
export const DEFAULT_THINKING_TEXT = "Thinking…";
export const DEFAULT_SPEAKING_TEXT = "Agent speaking — tap to end";

const HOST = `
  :host {
    all: initial;
${paletteCSS(DARK)}
    --speechify-agent-radius: ${RADIUS};
    --speechify-agent-orb-1: ${DEFAULT_ORB_COLOR_1};
    --speechify-agent-orb-2: ${DEFAULT_ORB_COLOR_2};
    display: inline-block;
    font: 14px/1.4 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    color-scheme: light dark;
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }
  :host([resolved]) {
    opacity: 1;
    pointer-events: auto;
  }
  :host([widget-theme="light"]) {
${paletteCSS(LIGHT)}
  }
  :host([widget-theme="auto"]) {
${paletteCSS(DARK)}
  }
  @media (prefers-color-scheme: light) {
    :host([widget-theme="auto"]) {
${paletteCSS(LIGHT, "      ")}
    }
  }
`;

// The collapsed pill and the FAB: the only control a visitor sees before a call.
const BUTTON = `
  button {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 20px 8px 8px;
    background: var(--speechify-agent-bg);
    color: var(--speechify-agent-fg);
    border-radius: var(--speechify-agent-radius);
    border: 1px solid var(--speechify-agent-border);
    box-shadow: var(--speechify-agent-shadow);
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: -0.01em;
    min-width: 140px;
    justify-content: center;
    transition: all 220ms cubic-bezier(0.4, 0, 0.2, 1);
    -webkit-tap-highlight-color: transparent;
  }
  button:hover {
    transform: translateY(-2px);
    box-shadow: var(--speechify-agent-shadow-hover);
  }
  /* The all:unset / all:initial resets above strip the UA focus ring, so
     without these a keyboard user sees nothing at all on any control.
     Painted as two rings — the accent over a contrasting outer edge — so the
     indicator survives on both themes and on the orb gradient. */
  button:focus-visible,
  .popup-ctrl:focus-visible,
  .terms-action:focus-visible,
  .terms-content:focus-visible {
    outline: 2px solid var(--speechify-agent-accent);
    outline-offset: 2px;
    box-shadow: 0 0 0 4px var(--speechify-agent-bg);
  }
  .terms-content:focus-visible {
    outline-offset: -2px;
  }
  button:active { transform: translateY(0); }
  button:disabled { opacity: 0.6; cursor: wait; }
  button.active {
    border-color: color-mix(in srgb, var(--speechify-agent-orb-1) 30%, transparent);
    box-shadow:
      var(--speechify-agent-shadow),
      0 0 20px 0 color-mix(in srgb, var(--speechify-agent-orb-1) 20%, transparent);
  }
  button.active:hover {
    box-shadow:
      var(--speechify-agent-shadow-hover),
      0 0 24px 2px color-mix(in srgb, var(--speechify-agent-orb-1) 25%, transparent);
  }
  button.connecting {
    animation: btn-shimmer 1.8s ease-in-out infinite;
  }
  button.fab {
    padding: 0;
    min-width: auto;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
  }
  button.fab:hover { box-shadow: none; }
  button.fab.active {
    border-color: transparent;
    box-shadow: 0 0 24px 4px color-mix(in srgb, var(--speechify-agent-orb-1) 30%, transparent);
  }
  @keyframes btn-shimmer {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

// The orb and every animation it runs. Gated on `.in-call` so a page carrying the widget is not animating forever at rest.
const ORB = `
  .orb-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  }
  .fab .orb-wrap {
    width: 100%;
    height: 100%;
  }
  .orb-wrap::before,
  .orb-wrap::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 1.5px solid var(--speechify-agent-orb-1);
    opacity: 0;
    pointer-events: none;
    transition: opacity 300ms ease;
  }
  .orb-wrap.speaking::before {
    animation: ring-expand 2s ease-out infinite;
  }
  .orb-wrap.speaking::after {
    animation: ring-expand 2s ease-out 0.8s infinite;
  }
  .orb-wrap.listening::before {
    animation: ring-pulse 2.4s ease-in-out infinite;
  }
  .orb-wrap.listening::after {
    opacity: 0;
  }
  .orb-wrap.thinking::before {
    animation: ring-rotate 2.4s linear infinite;
    border-style: dashed;
    opacity: 0.55;
  }
  .orb-wrap.thinking::after {
    opacity: 0;
  }
  @keyframes ring-expand {
    0% { transform: scale(1); opacity: 0.5; }
    100% { transform: scale(1.8); opacity: 0; }
  }
  @keyframes ring-pulse {
    0%, 100% { transform: scale(1); opacity: 0; }
    50% { transform: scale(1.25); opacity: 0.35; }
  }
  @keyframes ring-rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .orb {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background:
      linear-gradient(135deg,
        var(--speechify-agent-orb-1),
        var(--speechify-agent-orb-2));
    color: var(--speechify-agent-fg);
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms ease;
  }
  /* Idle breathing runs only while a call is live. As an unconditional rule
     on .orb it animated forever on every page carrying the widget, whether
     or not the visitor ever interacted, which is continuous compositor work
     and battery cost on someone else's site. */
  .orb.in-call {
    animation: orb-idle 3s ease-in-out infinite;
  }
  .orb.speaking {
    animation: orb-speak 1.1s ease-in-out infinite;
    box-shadow:
      0 0 16px 4px color-mix(in srgb, var(--speechify-agent-orb-1) 40%, transparent),
      0 0 32px 8px color-mix(in srgb, var(--speechify-agent-orb-2) 15%, transparent);
  }
  .orb.listening {
    animation: orb-listen 2.2s ease-in-out infinite;
    box-shadow: 0 0 12px 2px color-mix(in srgb, var(--speechify-agent-orb-1) 25%, transparent);
  }
  .orb.connecting {
    animation: orb-connecting 1.4s ease-in-out infinite;
  }
  .orb.thinking {
    animation: orb-thinking 1.6s ease-in-out infinite;
    box-shadow: 0 0 12px 2px color-mix(in srgb, var(--speechify-agent-orb-1) 30%, transparent);
  }
  .orb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  @keyframes orb-idle {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.03); }
  }
  @keyframes orb-speak {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.12); }
  }
  @keyframes orb-listen {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  @keyframes orb-connecting {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(0.92); opacity: 0.7; }
  }
  @keyframes orb-thinking {
    0%, 100% { transform: scale(0.98); }
    50% { transform: scale(1.04); }
  }
  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.9;
  }
  .indicator.pulse {
    animation: pulse 1.2s ease-in-out infinite;
  }
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.4); opacity: 0.4; }
  }
`;

// The inline error line under the button.
const HINT = `
  .hint {
    margin-top: 8px;
    color: var(--speechify-agent-muted);
    font-size: 12px;
    text-align: center;
  }

  /* ── Popup panel ── */
`;

// The in-call panel: transcript or orb display, plus the mute and end controls.
const POPUP = `
  .popup {
    display: none;
    flex-direction: column;
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    width: 320px;
    background: var(--speechify-agent-bg);
    border: 1px solid var(--speechify-agent-border);
    border-radius: 16px;
    box-shadow: var(--speechify-agent-shadow);
    overflow: hidden;
    z-index: 10;
    animation: popup-in 200ms ease;
  }
  .popup.open { display: flex; }
  .popup.pill-pos { left: 0; right: auto; min-width: 100%; }
  @keyframes popup-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .popup-body {
    flex: 1;
    overflow: hidden;
  }
  .popup-body .transcript-panel {
    position: static !important;
    width: auto !important;
    margin: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .popup-body .transcript-panel.open {
    max-height: 280px;
  }
  .popup-body .transcript-scroll {
    box-sizing: border-box;
    max-height: 280px;
  }
  .popup-orb-display {
    display: none;
    align-items: center;
    justify-content: center;
    padding: 32px;
  }
  .popup-orb-display .orb-wrap {
    width: 80px;
    height: 80px;
  }
  .popup-orb-display .orb-wrap::before,
  .popup-orb-display .orb-wrap::after {
    inset: -8px;
  }
  .popup-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px;
    border-top: 1px solid var(--speechify-agent-border);
  }
  .popup-ctrl {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 200ms ease;
    -webkit-tap-highlight-color: transparent;
  }
  .popup-ctrl:hover { transform: none; box-shadow: none; }
  /* display on .popup-ctrl beats the browser's default [hidden] rule, so a
     hidden control stays on screen as an empty pill. */
  .popup-ctrl[hidden] { display: none; }
  .popup-ctrl.mute-btn {
    width: 40px;
    height: 40px;
    min-width: auto;
    padding: 0;
    border-radius: 50%;
    background: var(--speechify-agent-border);
    color: var(--speechify-agent-fg);
    border: none;
  }
  .popup-ctrl.mute-btn.muted {
    background: #ef4444;
    color: #ffffff;
  }
  .popup-ctrl.mute-btn:hover { opacity: 0.8; }
  .popup-ctrl.speed-btn {
    height: 40px;
    min-width: 52px;
    padding: 0 12px;
    border-radius: 9999px;
    background: var(--speechify-agent-border);
    color: var(--speechify-agent-fg);
    border: none;
    font-weight: 500;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
  .popup-ctrl.speed-btn:hover { opacity: 0.8; }
  .popup-ctrl.speed-btn:disabled { opacity: 0.5; cursor: default; }
  .popup-ctrl.end-btn {
    height: 40px;
    min-width: auto;
    padding: 0 20px;
    gap: 6px;
    background: #ef4444;
    color: #ffffff;
    border-radius: 9999px;
    border: none;
    font-weight: 500;
    font-size: 14px;
  }
  .popup-ctrl.end-btn:hover { background: #dc2626; }
`;

// The pre-call terms modal and its overlay.
const TERMS = `
  .terms-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: none;
    align-items: flex-end;
    justify-content: center;
    z-index: 2147483647;
  }
  .terms-overlay.open { display: flex; }
  .terms-modal {
    box-sizing: border-box;
    width: min(560px, calc(100vw - 24px));
    max-height: min(80vh, 680px);
    margin: 12px;
    border-radius: 18px;
    background: #ffffff;
    color: #111827;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font: 16px/1.5 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  }
  .terms-content {
    padding: 20px;
    overflow: auto;
    word-break: break-word;
  }
  .terms-content > :first-child { margin-top: 0; }
  .terms-content > :last-child { margin-bottom: 0; }
  .terms-content h1,
  .terms-content h2,
  .terms-content h3,
  .terms-content h4 {
    margin: 18px 0 8px;
    font-weight: 600;
    line-height: 1.25;
  }
  .terms-content h1 { font-size: 1.5em; }
  .terms-content h2 { font-size: 1.3em; }
  .terms-content h3 { font-size: 1.15em; }
  .terms-content h4 { font-size: 1em; }
  .terms-content p { margin: 0 0 12px; }
  .terms-content ul,
  .terms-content ol { margin: 0 0 12px; padding-left: 24px; }
  .terms-content li { margin: 4px 0; }
  .terms-content blockquote {
    margin: 0 0 12px;
    padding: 4px 12px;
    border-left: 3px solid currentColor;
    opacity: 0.85;
  }
  .terms-content code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.92em;
    padding: 1px 4px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
  }
  .terms-content pre {
    margin: 0 0 12px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.06);
    overflow: auto;
  }
  .terms-content pre code {
    padding: 0;
    background: transparent;
    border-radius: 0;
  }
  .terms-content a {
    color: inherit;
    text-decoration: underline;
  }
  .terms-content hr {
    border: 0;
    border-top: 1px solid currentColor;
    opacity: 0.2;
    margin: 16px 0;
  }
  @media (prefers-color-scheme: dark) {
    .terms-content code,
    .terms-content pre { background: rgba(255, 255, 255, 0.08); }
  }
  .terms-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 0 20px 20px;
  }
  .terms-action {
    all: unset;
    box-sizing: border-box;
    border: 1px solid #d1d5db;
    border-radius: 9999px;
    padding: 8px 20px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
  }
  .terms-action.primary {
    background: #111827;
    border-color: #111827;
    color: #ffffff;
  }
  @media (prefers-color-scheme: dark) {
    .terms-modal {
      background: #1f2937;
      color: #f9fafb;
    }
    .terms-action {
      border-color: #4b5563;
      color: #f9fafb;
    }
    .terms-action.primary {
      background: #f9fafb;
      border-color: #f9fafb;
      color: #111827;
    }
  }

  /* Status is announced through this instead of the button's own accessible
     name: a live region ON an interactive control makes a screen reader
     re-announce the control every time the label changes, which during a
     call is every agent state transition. */
`;

// The screen-reader live region, and the reduced-motion opt-out that must come last so it overrides every animation above.
const A11Y = `
  .sr-status {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  /* The widget defines several infinite animations. Honouring this is a
     WCAG 2.3.3 obligation, and it runs on other people's sites where we do
     not get to decide the visitor's tolerance. Transitions are kept but
     collapsed so state changes stay perceivable without motion. */
  @media (prefers-reduced-motion: reduce) {
    :host,
    button,
    .orb,
    .orb-wrap::before,
    .orb-wrap::after,
    .indicator,
    .popup,
    .popup-ctrl,
    .transcript-bubble {
      animation: none !important;
      transition-duration: 0.01ms !important;
    }
    button:hover {
      transform: none;
    }
  }
`;

/**
 * The stylesheet injected into the shadow root, composed from the blocks
 * above. Order matters in exactly one place: A11Y carries the
 * prefers-reduced-motion opt-out, which must come last to override the
 * animations every earlier block defines.
 */
export const STYLES = [HOST, BUTTON, ORB, HINT, POPUP, TERMS, A11Y].join("\n");
